import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Loader2, Sparkles, Flame, Star, Gem } from "lucide-react";
import { useCartStore, type ShopifyProduct } from "@/stores/cartStore";
import { useLang } from "@/contexts/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

interface ProductCardProps {
  product: ShopifyProduct;
}

export const ProductCard = ({ product }: ProductCardProps) => {
  const addItem = useCartStore(state => state.addItem);
  const isLoading = useCartStore(state => state.isLoading);
  const { lang, t } = useLang();
  const cardRef = useRef<HTMLAnchorElement>(null);
  const [isHover, setIsHover] = useState(false);
  const { node } = product;
  const selectedVariant = node.variants.edges[0]?.node;
  const image = node.images.edges[0]?.node;
  const price = node.priceRange.minVariantPrice;
  const productPath = lang === "en" ? `/en/product/${node.handle}` : `/product/${node.handle}`;

  // Dynamic dual-badge logic — Nouveau (≤14d) / Bestseller (tag or keyword)
  const tags = (node.tags || []).map(t => t.toLowerCase());
  const titleLower = node.title.toLowerCase();
  const isNew = (() => {
    if (tags.includes("new") || tags.includes("nouveau")) return true;
    if (!node.createdAt) return false;
    const ageDays = (Date.now() - new Date(node.createdAt).getTime()) / 86_400_000;
    return ageDays <= 14;
  })();
  const isBestseller =
    tags.includes("bestseller") ||
    tags.includes("best-seller") ||
    tags.includes("populaire") ||
    titleLower.includes("mongela");
  const isLimited =
    tags.includes("limited") ||
    tags.includes("limited-edition") ||
    tags.includes("edition-limitee") ||
    tags.includes("édition-limitée") ||
    tags.includes("limitee") ||
    tags.includes("limitée");
  // Priority: Limited > Bestseller > Nouveau (only one badge shown)
  const dynamicBadge = isLimited
    ? { label: t({ fr: "Édition limitée", en: "Limited edition" }), icon: Gem, gradient: "from-foreground to-primary", glow: "shadow-foreground/40" }
    : isBestseller
      ? { label: t({ fr: "Bestseller", en: "Bestseller" }), icon: Flame, gradient: "from-terracotta-dark to-primary", glow: "shadow-primary/40" }
      : isNew
        ? { label: t({ fr: "Nouveau", en: "New" }), icon: Star, gradient: "from-gold-dark to-gold", glow: "shadow-gold/40" }
        : null;

  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const card = cardRef.current;
    if (!card) return;
    if (window.matchMedia("(hover: none)").matches) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Track cursor position only (for soft glare), no tilt
    card.style.setProperty("--mx", `${(x / rect.width) * 100}%`);
    card.style.setProperty("--my", `${(y / rect.height) * 100}%`);
  };

  const handleMouseEnter = () => setIsHover(true);
  const handleMouseLeave = () => setIsHover(false);

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedVariant) return;
    await addItem({
      product,
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      quantity: 1,
      selectedOptions: selectedVariant.selectedOptions || [],
      productType: 'sticker',
    });
    trackEvent("add_to_cart", {
      handle: node.handle,
      title: node.title,
      pack_size: 1,
      unit_price: parseFloat(selectedVariant.price.amount),
      total_value: parseFloat(selectedVariant.price.amount),
      currency: selectedVariant.price.currencyCode,
      source: "product_card",
    });
    toast.success(t({ fr: "Ajouté au panier !", en: "Added to cart!" }), { position: "top-center" });
  };

  return (
    <Link
      ref={cardRef}
      to={productPath}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        transition: "box-shadow 600ms ease, border-color 600ms ease",
      }}
      className="group relative block rounded-3xl overflow-hidden bg-gradient-to-b from-card via-card to-secondary/30 shadow-editorial hover:shadow-card-hover will-change-transform ring-1 ring-border/60 hover:ring-primary/50 transition-[box-shadow,ring-color] duration-500"
    >
      {/* Premium gradient outer frame — terracotta → gold → terracotta */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-3xl z-[5] opacity-70 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background:
            "linear-gradient(135deg, hsl(var(--primary) / 0.55), hsl(var(--gold) / 0.7) 45%, hsl(var(--primary) / 0.55)) border-box",
          WebkitMask:
            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          padding: "1.5px",
        }}
      />
      {/* Inner cream liseré for double-frame premium feel */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-[3px] rounded-[1.35rem] z-[5] ring-1 ring-cream/40 group-hover:ring-cream/70 transition-all duration-500"
      />
      {/* Premium gradient ambient glow on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-gold/0 to-primary/0 group-hover:from-primary/8 group-hover:via-gold/15 group-hover:to-primary/8 transition-all duration-700 pointer-events-none z-[1]" />

      {/* Cursor-following soft glare (white only, no rainbow) */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[3] opacity-0 group-hover:opacity-100 transition-opacity duration-500 mix-blend-overlay"
        style={{
          background:
            "radial-gradient(circle at var(--mx,50%) var(--my,50%), hsl(0 0% 100% / 0.35), transparent 40%)",
        }}
      />

      {/* Premium badge top-left — glassmorphism */}
      <div
        className="absolute top-2 left-2 sm:top-4 sm:left-4 z-10 flex items-center gap-1 sm:gap-1.5 bg-foreground/70 backdrop-blur-xl text-background text-[8px] sm:text-[10px] font-body font-bold px-2 py-1 sm:px-3 sm:py-1.5 rounded-full shadow-xl border border-gold/40 uppercase tracking-[0.1em] sm:tracking-[0.12em]"
        style={{ transform: "translateZ(60px)" }}
      >
        <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gold" />
        <span className="bg-gradient-to-r from-gold-light to-gold bg-clip-text text-transparent">Premium</span>
      </div>

      {/* Dynamic badge — Nouveau / Bestseller, sits below Premium */}
      {dynamicBadge && (
        <div
          className={`absolute top-10 left-2 sm:top-14 sm:left-4 z-10 flex items-center gap-1 bg-gradient-to-r ${dynamicBadge.gradient} text-primary-foreground text-[8px] sm:text-[10px] font-body font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full shadow-lg ${dynamicBadge.glow} uppercase tracking-[0.1em] sm:tracking-[0.14em] animate-fade-in`}
          style={{ transform: "translateZ(58px)" }}
        >
          <dynamicBadge.icon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
          {dynamicBadge.label}
        </div>
      )}

      {/* Cultural accent — maple leaf in glass disc */}
      <div
        className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 w-7 h-7 sm:w-10 sm:h-10 rounded-full bg-foreground/70 backdrop-blur-xl flex items-center justify-center text-xs sm:text-base shadow-xl border border-gold/30 transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 group-hover:border-gold/70"
        style={{ transform: "translateZ(70px)" }}
      >
        🍁
      </div>

      {/* Image frame — inset matted look with portrait ratio for premium gallery feel */}
      <div className="relative px-2 pt-2 pb-1 sm:px-3 sm:pt-3" style={{ transformStyle: "preserve-3d" }}>
        <div
          className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-gradient-to-br from-secondary via-cream to-secondary ring-1 ring-foreground/5 shadow-inner"
          style={{ transform: "translateZ(40px)", transformStyle: "preserve-3d" }}
        >
          {image ? (
            <>
              <img
                src={`${image.url}&width=612`}
                alt={image.altText || node.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                width="306"
                height="382"
                loading="lazy"
                style={{ transform: "translateZ(20px)" }}
              />
              {/* Vignette */}
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              {/* Shimmer sweep */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-warm-white/30 to-transparent" />
              {/* Inner gold liseré on the photo */}
              <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gold/20 group-hover:ring-gold/40 transition-colors duration-500 pointer-events-none" />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <ShoppingCart className="w-12 h-12" />
            </div>
          )}
        </div>
      </div>

      {/* Content with parallax layers */}
      <div className="relative px-3 pt-3 pb-3 sm:px-5 sm:pt-4 sm:pb-5 space-y-2.5 sm:space-y-4 z-[2]" style={{ transformStyle: "preserve-3d" }}>
        {/* Eyebrow tag */}
        <div className="flex items-center gap-2" style={{ transform: "translateZ(30px)" }}>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 sm:px-2 rounded-full bg-primary/10 text-primary text-[8px] sm:text-[9px] font-body font-bold uppercase tracking-[0.14em] sm:tracking-[0.18em]">
            <span className="w-1 h-1 rounded-full bg-primary animate-pulse" />
            {t({ fr: "Adhésif 3D", en: "3D Sticker" })}
          </span>
        </div>

        <div className="space-y-1 sm:space-y-1.5">
          <h3
            className="font-display font-bold text-sm sm:text-xl leading-tight tracking-tight truncate group-hover:text-primary transition-colors duration-300"
            style={{ transform: "translateZ(50px)" }}
          >
            {node.title}
          </h3>
        </div>

        {/* Price + CTA bar — modern split */}
        <div
          className="flex items-stretch gap-1.5 sm:gap-2 pt-1"
          style={{ transform: "translateZ(45px)" }}
        >
          <div className="flex flex-col justify-center px-2 sm:px-4 rounded-xl sm:rounded-2xl bg-gradient-to-br from-secondary to-cream border border-gold/20 shadow-sm">
            <span className="hidden sm:inline text-[9px] font-body font-semibold text-muted-foreground uppercase tracking-wider leading-none">
              {t({ fr: "Dès", en: "From" })}
            </span>
            <span className="text-sm sm:text-xl font-display font-bold bg-gradient-to-br from-primary to-terracotta-dark bg-clip-text text-transparent leading-tight tabular-nums">
              {parseFloat(price.amount).toFixed(2)}
              <span className="text-[10px] sm:text-xs ml-0.5 text-primary/80">$</span>
            </span>
          </div>
          <Button
            onClick={handleAddToCart}
            disabled={isLoading || !selectedVariant?.availableForSale}
            className="flex-1 h-auto min-h-9 sm:min-h-10 px-2 sm:px-4 bg-gradient-to-br from-primary via-terracotta to-terracotta-dark text-primary-foreground hover:shadow-xl hover:shadow-primary/50 transition-all duration-300 rounded-xl sm:rounded-2xl font-display font-bold tracking-wide text-xs sm:text-sm group-hover:scale-[1.02]"
            aria-label={t({ fr: "Ajouter au panier", en: "Add to cart" })}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <ShoppingCart className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline sm:inline">{t({ fr: "Ajouter", en: "Add" })}</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Bottom under-glow shadow — warm gold tint */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-6 left-1/2 -translate-x-1/2 w-3/4 h-8 rounded-full opacity-0 group-hover:opacity-60 transition-opacity duration-500 blur-2xl"
        style={{
          background:
            "radial-gradient(ellipse, hsl(var(--gold) / 0.5), transparent 70%)",
        }}
      />
    </Link>
  );
};
